import React, { useState, useEffect } from 'react';
import {
  Search,
  ChevronDown,
  X,
  Trophy,
  Plus,
  Trash2,
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  Clipboard,
  Download,
  AlertCircle,
  FileText,
  User,
  Mail,
  Phone,
  BookOpen,
  GraduationCap,
  RefreshCw,
  Edit2,
  Lock
} from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import CollegeHomeSideBar from '../BasicComponents/CollegeHomeSideBar';
import CollegeHeader from '../BasicComponents/CollegeHeader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com';

const BRANCHES = [
    "Aeronautical Engineering",
    "Aerospace Engineering",
    "Agricultural Engineering",
    "Architecture",
    "Artificial Intelligence & Data Science (AI & DS)",
    "Artificial Intelligence & Machine Learning (AI & ML)",
    "Automobile Engineering",
    "B.Com",
    "BBA",
    "BCA",
    "Biomedical Engineering",
    "Biotechnology",
    "Chemical Engineering",
    "Civil Engineering",
    "CSE",
    "CSE (AI & ML)",
    "CSE (Cyber Security)",
    "CSE (Data Science)",
    "CSE (IoT)",
    "Diploma",
    "Electrical & Electronics Engineering (EEE)",
    "Electronics & Communication Engineering (ECE)",
    "Electronics & Instrumentation Engineering (EIE)",
    "Environmental Engineering",
    "Food Technology",
    "Industrial Engineering",
    "Information Technology (IT)",
    "M.Sc",
    "M.Tech",
    "MBA",
    "MCA",
    "Mechanical Engineering",
    "Mechatronics Engineering",
    "Metallurgical Engineering",
    "Mining Engineering",
    "Others",
    "Petroleum Engineering",
    "Pharmacy",
    "Production Engineering",
    "Robotics & Automation",
    "Textile Engineering"
];

const DepartmentDropdown = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOther, setIsOther] = useState(false);

    useEffect(() => {
        if (value && !BRANCHES.includes(value) && value !== 'Others') {
            setIsOther(true);
        } else if (value === 'Others') {
            setIsOther(true);
        } else {
            setIsOther(false);
        }
    }, [value]);

    const filteredBranches = BRANCHES.filter(b => 
        b.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="relative">
            {!isOther ? (
                <div className="relative">
                    <BookOpen size={14} className="absolute left-3 top-3 text-gray-400 z-10" />
                    <div 
                        onClick={() => setIsOpen(!isOpen)}
                        className="w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none cursor-pointer flex items-center justify-between min-h-[36px]"
                    >
                        <span className={value ? 'text-gray-800' : 'text-gray-400'}>
                            {value || 'Select Department'}
                        </span>
                        <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {isOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-xl max-h-60 overflow-hidden flex flex-col">
                            <div className="p-2 border-b border-gray-50 flex items-center gap-2">
                                <Search size={14} className="text-gray-400" />
                                <input 
                                    type="text"
                                    placeholder="Search..."
                                    className="w-full text-sm outline-none bg-transparent"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div className="overflow-y-auto flex-1">
                                {filteredBranches.map((branch) => (
                                    <div 
                                        key={branch}
                                        onClick={() => {
                                            if (branch === 'Others') {
                                                setIsOther(true);
                                                onChange('');
                                            } else {
                                                onChange(branch);
                                            }
                                            setIsOpen(false);
                                            setSearchTerm('');
                                        }}
                                        className="px-4 py-2 text-sm hover:bg-purple-50 cursor-pointer text-gray-700 hover:text-purple-700 transition-colors"
                                    >
                                        {branch}
                                    </div>
                                ))}
                                {filteredBranches.length === 0 && (
                                    <div className="px-4 py-3 text-xs text-gray-400 text-center">No results found</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="relative">
                    <BookOpen size={14} className="absolute left-3 top-3 text-gray-400" />
                    <input 
                        type="text"
                        placeholder="Enter department name"
                        value={value === 'Others' ? '' : value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                        autoFocus
                    />
                    <button 
                        onClick={() => {
                            setIsOther(false);
                            onChange('CSE');
                        }}
                        className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

const CollegeChallenge = () => {
  // Step 0: Student Nomination | 1: AI Interviews | 2: AI Evaluation | 3: Scholarship Result
  const [activeStep, setActiveStep] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [success, setSuccess] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isNominating, setIsNominating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isGeneratingCoupon, setIsGeneratingCoupon] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [nominationResult, setNominationResult] = useState(null); // result from /nominate endpoint

  // Common interview schedule (shared across all nominated students)
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewTimezone, setInterviewTimezone] = useState('Asia/Kolkata');
  const [scheduleErrors, setScheduleErrors] = useState({});

  // UI-only state for enterprise list toolbar — does not affect any business logic
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [listFilterDept, setListFilterDept] = useState('');
  const [listFilterYear, setListFilterYear] = useState('');
  const [listFilterStatus, setListFilterStatus] = useState('');
  const [listSortBy, setListSortBy] = useState('name');
  const [selectedRows, setSelectedRows] = useState(new Set());

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId = user.id || user.userId;
  console.log('CollegeChallenge User Context:', { user, collegeId });

  // ── Early derivation for dynamic stepper subtitle ───────────────────────
  const _IV_DONE_STATUSES = ['COMPLETED','INTERVIEW_COMPLETED','AI_EVALUATION_PENDING','AI_EVALUATION_COMPLETED'];
  const _allIvDone = students.length === 5 &&
      students.every(s => _IV_DONE_STATUSES.includes((s.interview_status || '').toUpperCase()));

  const steps = [
    { label: 'Student Nomination', description: 'Nominate 5 Students' },
    { label: 'AI Interviews', description: _allIvDone ? 'All Interviews Done' : 'Interviews in Progress' },
    { label: 'AI Evaluation', description: 'Evaluating Performance' },
    { label: 'Scholarship Result', description: 'Claim Scholarship' }
  ];

  useEffect(() => {
    console.log('CollegeChallenge mounted. collegeId:', collegeId);
    fetchChallengeStatus();
    // Load draft from localStorage
    const savedDraft = localStorage.getItem(`challenge_draft_${collegeId}`);
    if (savedDraft) {
        console.log('Found saved draft in localStorage');
        try {
            const parsed = JSON.parse(savedDraft);
            if (parsed && parsed.students && Array.isArray(parsed.students) && parsed.students.length > 0) {
                setStudents(parsed.students);
                setFieldErrors(parsed.students.map(() => ({})));
                if (parsed.interviewDate) setInterviewDate(parsed.interviewDate);
                if (parsed.interviewTime) setInterviewTime(parsed.interviewTime);
                if (parsed.interviewTimezone) setInterviewTimezone(parsed.interviewTimezone);
            } else if (parsed && Array.isArray(parsed) && parsed.length > 0) {
                // Legacy draft format (just an array of students)
                setStudents(parsed);
                setFieldErrors(parsed.map(() => ({})));
            }
        } catch (e) {
            console.error("Failed to load draft", e);
        }
    }
  }, [collegeId]);

  useEffect(() => {
    if (activeStep === 0 && challenge?.student_nomination_completed) {
        // Nomination is locked — clear any stale draft
        localStorage.removeItem(`challenge_draft_${collegeId}`);
        return;
    }
    if (students.length > 0 && activeStep === 0) {
        localStorage.setItem(`challenge_draft_${collegeId}`, JSON.stringify({
            students,
            interviewDate,
            interviewTime,
            interviewTimezone,
        }));
        if (!hasUnsavedChanges) {
            setHasUnsavedChanges(true);
        }
    }
  }, [students, interviewDate, interviewTime, interviewTimezone, collegeId, activeStep, challenge?.student_nomination_completed]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && activeStep === 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, activeStep]);

  // ── Auto-refresh interview status every 30 s when on the AI Interviews tab ──
  // Stops polling once all students have a terminal interview status.
  useEffect(() => {
    if (activeStep !== 1 || !challenge?.id) return;

    const TERMINAL = ['COMPLETED', 'INTERVIEW_COMPLETED', 'AI_EVALUATION_COMPLETED', 'SCHEDULE_FAILED', 'FAILED'];
    const allTerminal = students.length > 0 && students.every(s => TERMINAL.includes((s.interview_status || '').toUpperCase()));
    if (allTerminal) return; // nothing left to poll

    const timerId = setInterval(() => {
      handleRefreshStatus();
    }, 30000);

    return () => clearInterval(timerId);
  }, [activeStep, challenge?.id, students]);

  // ── Auto-navigate to AI Evaluation tab when all interviews are completed ──
  // Triggers whether via auto-refresh or manual Refresh Now button.
  useEffect(() => {
    if (activeStep !== 1 || students.length !== 5) return;
    const INTERVIEW_COMPLETE_STATUSES = [
        'COMPLETED', 'INTERVIEW_COMPLETED', 'AI_EVALUATION_PENDING', 'AI_EVALUATION_COMPLETED',
    ];
    const allDone = students.every(s => INTERVIEW_COMPLETE_STATUSES.includes((s.interview_status || '').toUpperCase()));
    if (allDone) {
        setActiveStep(2);
    }
  }, [students, activeStep]);

  const fetchChallengeStatus = async () => {
    console.log('Fetching challenge status for collegeId:', collegeId);
    setLoading(true);
    try {
        // First try to find active challenge
        const res = await axios.get(`${API_BASE_URL}/api/college-challenge/status/active?collegeId=${collegeId}`);
        console.log('Active challenge response:', res.data);
        if (res.data && res.data.challenge) {
            setChallenge(res.data.challenge);
            const normalizedStudents = (res.data.students || []).map(s => {
                const nameParts = (s.student_name || '').trim().split(' ');
                const first_name = s.first_name || nameParts[0] || '';
                const last_name = s.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');
                return {
                    ...s,
                    first_name,
                    last_name,
                    primary_skills: Array.isArray(s.primary_skills)
                        ? s.primary_skills.join(', ')
                        : (s.primary_skills || ''),
                    year: s.year !== undefined && s.year !== null ? String(s.year) : '',
                    department: s.department || 'CSE'
                };
            });
            // Restore common interview schedule from challenge record if available
            const ch = res.data.challenge;
            if (ch.preferred_interview_date) setInterviewDate(String(ch.preferred_interview_date).split('T')[0]);
            if (ch.preferred_interview_time) setInterviewTime(String(ch.preferred_interview_time).substring(0, 5));
            if (ch.timezone) setInterviewTimezone(ch.timezone);
            if (ch.coupon_generated && ch.coupon_code) {
                setCouponData({ coupon_code: ch.coupon_code, discount_percentage: ch.scholarship_percentage });
            }
            setStudents(normalizedStudents);
            setFieldErrors(normalizedStudents.map(() => ({})));
            mapStatusToStep(res.data.challenge);
        } else {
            console.log('No challenge object in response, creating a new one');
            // No active challenge, create one automatically to avoid blank screen
            await handleCreateChallenge();
        }
    } catch (err) {
        console.error('Error fetching challenge status:', err);
        // If 404, it means no challenge yet, create one
        if (err.response?.status === 404) {
            console.log('No active challenge found (404), creating new challenge');
            await handleCreateChallenge();
        } else {
            setError(err.response?.data?.error || 'Failed to fetch challenge status. Please refresh the page.');
        }
    } finally {
        setLoading(false);
    }
  };

  const mapStatusToStep = (challenge) => {
      const step = challenge?.current_step || 'STUDENT_NOMINATION';
      console.log('Mapping current_step to tab:', step);
      if (step === 'STUDENT_NOMINATION') setActiveStep(0);
      else if (step === 'AI_INTERVIEW') setActiveStep(1);
      else if (step === 'AI_EVALUATION') setActiveStep(2);
      else if (step === 'SCHOLARSHIP' || step === 'COMPLETED') setActiveStep(3);
      setHasUnsavedChanges(false);
  };

  const handleCreateChallenge = async () => {
    setLoading(true);
    try {
        const res = await axios.post(`${API_BASE_URL}/api/college-challenge/create`, {
            collegeId,
            createdBy: user.username
        });
        setChallenge(res.data);
        setActiveStep(0);
        setStudents([]);
    } catch (err) {
        setError(err.response?.data?.error || 'Failed to create challenge');
    } finally {
        setLoading(false);
    }
  };

  const handleAddStudent = () => {
    if (students.length >= 5) return;
    const newStudent = {
        first_name: '',
        last_name: '',
        student_id: '',
        email: '',
        phone: '',
        department: 'CSE',
        year: '1',
        primary_skills: '',
    };
    const newStudents = [...students, newStudent];
    setStudents(newStudents);
    setFieldErrors([...fieldErrors, {
        department_valid: true,
        year_valid: true
    }]);
  };

  const handleRemoveStudent = (index) => {
    setStudents(students.filter((_, i) => i !== index));
    setFieldErrors(fieldErrors.filter((_, i) => i !== index));
  };

  const handleStudentChange = (index, field, value) => {
    const updated = [...students];
    let newValue = value;

    if (field === 'first_name' || field === 'last_name') {
        // Auto-capitalize first letter, allow only letters and spaces
        newValue = value.replace(/[^a-zA-Z\s]/g, '').replace(/\s\s+/g, ' ');
        if (newValue.length > 0) {
            newValue = newValue.charAt(0).toUpperCase() + newValue.slice(1);
        }
    } else if (field === 'phone') {
        newValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (field === 'student_id') {
        newValue = value.replace(/[^a-zA-Z0-9\-/]/g, '').slice(0, 30);
    }

    updated[index][field] = newValue;
    setStudents(updated);

    // Real-time validation for this field
    validateField(index, field, newValue, updated);
  };

  const validateField = (index, field, value, currentStudents = students) => {
      const updatedErrors = [...fieldErrors];
      if (!updatedErrors[index]) updatedErrors[index] = {};
      
      const student = currentStudents[index];
      const studentErrors = { ...updatedErrors[index] };

      // Helper for duplicate checks
      const isDuplicate = (val, fld) => {
          return currentStudents.some((s, i) => i !== index && s[fld]?.trim().toLowerCase() === val?.trim().toLowerCase());
      };

      switch (field) {
          case 'first_name':
              const firstName = value.trim();
              if (!firstName || firstName.length < 2 || firstName.length > 50 || !/^[a-zA-Z\s]+$/.test(firstName)) {
                  studentErrors.first_name = "Please enter a valid first name.";
                  delete studentErrors.first_name_valid;
              } else {
                  delete studentErrors.first_name;
                  studentErrors.first_name_valid = true;
              }
              break;
          case 'last_name':
              const lastName = value.trim();
              if (!lastName || lastName.length < 2 || lastName.length > 50 || !/^[a-zA-Z\s]+$/.test(lastName)) {
                  studentErrors.last_name = "Please enter a valid last name.";
                  delete studentErrors.last_name_valid;
              } else {
                  delete studentErrors.last_name;
                  studentErrors.last_name_valid = true;
              }
              break;
          case 'student_id':
              const roll = value.trim();
              if (!roll || roll.length < 3 || roll.length > 30 || !/^[a-zA-Z0-9\-/]+$/.test(roll)) {
                  studentErrors.student_id = "Please enter a valid Student Roll Number.";
              } else if (isDuplicate(roll, 'student_id')) {
                  studentErrors.student_id = "This student has already been added to the challenge.";
              } else {
                  delete studentErrors.student_id;
                  studentErrors.student_id_valid = true;
              }
              break;
          case 'email':
              const email = value.trim();
              const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
              if (!email || !emailRegex.test(email)) {
                  studentErrors.email = "Please enter a valid email address.";
              } else if (isDuplicate(email, 'email')) {
                  studentErrors.email = "This email has already been nominated.";
              } else {
                  delete studentErrors.email;
                  studentErrors.email_valid = true;
              }
              break;
          case 'phone':
              const phone = value.trim();
              if (!phone || !/^[6-9]\d{9}$/.test(phone)) {
                  studentErrors.phone = "Please enter a valid 10-digit mobile number.";
              } else if (isDuplicate(phone, 'phone')) {
                  studentErrors.phone = "This phone number has already been nominated.";
              } else {
                  delete studentErrors.phone;
                  studentErrors.phone_valid = true;
              }
              break;
          case 'department':
              if (!value) {
                  studentErrors.department = "Please select a department.";
              } else {
                  delete studentErrors.department;
                  studentErrors.department_valid = true;
              }
              break;
          case 'year':
              if (!value) {
                  studentErrors.year = "Please select the student's academic year.";
              } else {
                  delete studentErrors.year;
                  studentErrors.year_valid = true;
              }
              break;
          case 'primary_skills':
              if (!value.trim()) {
                  studentErrors.primary_skills = "Skills are required";
              } else {
                  delete studentErrors.primary_skills;
                  studentErrors.primary_skills_valid = true;
              }
              break;
          default:
              break;
      }

      updatedErrors[index] = studentErrors;
      setFieldErrors(updatedErrors);
  };

  const validateStudents = () => {
      const errors = [];
      let isValid = true;
      const rollNumbers = new Set();
      const emails = new Set();
      const phones = new Set();

      students.forEach((student, index) => {
          const studentErrors = {};
          
          // First Name validation
          const firstName = (student.first_name || '').trim();
          if (!firstName) {
              studentErrors.first_name = "Please enter a valid first name.";
          } else if (firstName.length < 2 || firstName.length > 50) {
              studentErrors.first_name = "Please enter a valid first name.";
          } else if (!/^[a-zA-Z\s]+$/.test(firstName)) {
              studentErrors.first_name = "Please enter a valid first name.";
          }

          // Last Name validation
          const lastName = (student.last_name || '').trim();
          if (!lastName) {
              studentErrors.last_name = "Please enter a valid last name.";
          } else if (lastName.length < 2 || lastName.length > 50) {
              studentErrors.last_name = "Please enter a valid last name.";
          } else if (!/^[a-zA-Z\s]+$/.test(lastName)) {
              studentErrors.last_name = "Please enter a valid last name.";
          }
          
          // Student Roll Number (student_id) validation
          const rollNumber = student.student_id.trim();
          if (!rollNumber) {
              studentErrors.student_id = "Please enter a valid Student Roll Number.";
          } else if (rollNumber.length < 3 || rollNumber.length > 30) {
              studentErrors.student_id = "Please enter a valid Student Roll Number.";
          } else if (!/^[a-zA-Z0-9\-/]+$/.test(rollNumber)) {
              studentErrors.student_id = "Please enter a valid Student Roll Number.";
          } else if (rollNumbers.has(rollNumber.toLowerCase())) {
              studentErrors.student_id = "This student has already been added to the challenge.";
          }
          if (rollNumber) rollNumbers.add(rollNumber.toLowerCase());

          // Email validation
          const email = student.email.trim();
          const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
          if (!email) {
              studentErrors.email = "Please enter a valid email address.";
          } else if (!emailRegex.test(email)) {
              studentErrors.email = "Please enter a valid email address.";
          } else if (emails.has(email.toLowerCase())) {
              studentErrors.email = "This student has already been added to the challenge.";
          }
          if (email) emails.add(email.toLowerCase());

          // Phone validation
          const phone = student.phone.trim();
          if (!phone) {
              studentErrors.phone = "Please enter a valid 10-digit mobile number.";
          } else if (!/^[6-9]\d{9}$/.test(phone)) {
              studentErrors.phone = "Please enter a valid 10-digit mobile number.";
          } else if (phones.has(phone)) {
              studentErrors.phone = "This student has already been added to the challenge.";
          }
          if (phone) phones.add(phone);

          if (!student.department) studentErrors.department = "Please select a department.";
          if (!student.year) studentErrors.year = "Please select the student's academic year.";
          const skillsVal = Array.isArray(student.primary_skills) ? student.primary_skills.join(', ') : (student.primary_skills || '');
          if (!skillsVal.trim()) studentErrors.primary_skills = "Skills are required";

          if (Object.keys(studentErrors).length > 0) {
              isValid = false;
          }
          errors[index] = studentErrors;
      });

      setFieldErrors(errors);
      return isValid;
  };

  // Normalize a student row from the API into the local state shape
  const _normalizeStudent = (s) => {
      const nameParts = (s.student_name || '').trim().split(' ');
      return {
          ...s,
          first_name: s.first_name || nameParts[0] || '',
          last_name: s.last_name || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : ''),
          primary_skills: Array.isArray(s.primary_skills) ? s.primary_skills.join(', ') : (s.primary_skills || ''),
          year: s.year !== undefined && s.year !== null ? String(s.year) : '',
          department: s.department || 'CSE',
      };
  };

  /**
   * Validate the common interview schedule fields.
   * Returns true if valid; false and populates scheduleErrors otherwise.
   */
  const validateSchedule = () => {
      const errs = {};

      if (!interviewDate) {
          errs.date = "Preferred Interview Date is required.";
      } else {
          const selectedDate = new Date(interviewDate);
          selectedDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const maxDate = new Date();
          maxDate.setDate(today.getDate() + 7);
          maxDate.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
              errs.date = "Interview date cannot be in the past.";
          } else if (selectedDate > maxDate) {
              errs.date = "Interview can only be scheduled within the next 7 days.";
          }
      }

      if (!interviewTime) {
          errs.time = "Preferred Interview Time is required.";
      } else if (interviewDate) {
          const todayStr = new Date().toISOString().split('T')[0];
          if (interviewDate === todayStr) {
              const [hours, minutes] = interviewTime.split(':').map(Number);
              const selectedTime = new Date();
              selectedTime.setHours(hours, minutes, 0, 0);
              const minTime = new Date();
              minTime.setMinutes(minTime.getMinutes() + 30);
              if (selectedTime < minTime) {
                  errs.time = "Please select a time at least 30 minutes from now.";
              }
          }
      }

      setScheduleErrors(errs);
      return Object.keys(errs).length === 0;
  };

  /**
   * Nominate students — runs Exihire registration only (Step 1a).
   * Navigates to the Registration Status tab on completion.
   */
  const handleNominateStudents = async () => {
      if (students.length !== 5) {
          setError("Exactly 5 students are required.");
          return;
      }
      if (!validateSchedule()) {
          setError("Please select a common interview date and time for all nominated students.");
          return;
      }
      if (!validateStudents()) {
          setError("Please fill all required fields correctly.");
          return;
      }

      setIsNominating(true);
      setError(null);
      setSuccess(null);
      setNominationResult(null);

      try {
          const payload = {
              challengeId: challenge.id,
              collegeId,
              preferredInterviewDate: interviewDate,
              preferredInterviewTime: interviewTime,
              timezone: interviewTimezone,
              students: students.map(s => ({
                  first_name: (s.first_name || '').trim(),
                  last_name: (s.last_name || '').trim(),
                  student_name: `${(s.first_name || '').trim()} ${(s.last_name || '').trim()}`.trim(),
                  student_id: s.student_id,
                  email: s.email,
                  phone: s.phone,
                  department: s.department,
                  year: s.year,
                  primary_skills: s.primary_skills,
              })),
          };

          const res = await axios.post(`${API_BASE_URL}/api/college-challenge/nominate`, payload, {
              headers: { 'Content-Type': 'application/json' },
              timeout: 60000,
          });

          const data = res.data;
          setNominationResult(data);
          localStorage.removeItem(`challenge_draft_${collegeId}`);
          setHasUnsavedChanges(false);

          if (data.students && Array.isArray(data.students)) {
              setStudents(data.students.map(_normalizeStudent));
              setFieldErrors(data.students.map(() => ({})));
          }

          // Always navigate to Step 1 (Registration Status tab)
          setSuccess(data.message || 'Students registered successfully!');
          setActiveStep(1);
      } catch (err) {
          const errMsg = err.response?.data?.error || err.message || 'Failed to nominate students. Please try again.';
          setError(errMsg);
      } finally {
          setIsNominating(false);
      }
  };

  /**
   * Refresh interview status by re-fetching the latest challenge + student data from DB.
   * Auto-navigates to AI Evaluation tab when all 5 interviews are completed.
   */
  const handleRefreshStatus = async () => {
      if (!challenge?.id) return;
      setIsRefreshing(true);
      setError(null);
      try {
          const res = await axios.get(`${API_BASE_URL}/api/college-challenge/status/active?collegeId=${collegeId}`);
          const data = res.data;

          let updatedStudents = students;
          if (data.students && Array.isArray(data.students)) {
              updatedStudents = data.students.map(_normalizeStudent);
              setStudents(updatedStudents);
              setFieldErrors(updatedStudents.map(() => ({})));
          }
          if (data.challenge) {
              setChallenge(data.challenge);
          }

          setSuccess(`Status refreshed. ${updatedStudents.length} student(s) updated.`);

          // Auto-navigate to AI Evaluation tab when all interviews are complete
          const INTERVIEW_COMPLETE_STATUSES = [
              'COMPLETED', 'INTERVIEW_COMPLETED', 'AI_EVALUATION_PENDING', 'AI_EVALUATION_COMPLETED',
          ];
          const allDone = updatedStudents.length === 5 &&
              updatedStudents.every(s => INTERVIEW_COMPLETE_STATUSES.includes((s.interview_status || '').toUpperCase()));
          if (allDone && activeStep === 1) {
              setActiveStep(2);
          }
      } catch (err) {
          setError(err.response?.data?.error || 'Failed to refresh status. Please try again.');
      } finally {
          setIsRefreshing(false);
      }
  };

  /**
   * Trigger AI evaluation for all students with completed interviews.
   * Calls POST /api/college-challenge/evaluate
   */
  const handleEvaluatePerformance = async () => {
      if (!challenge?.id) return;
      setIsEvaluating(true);
      setError(null);
      try {
          const res = await axios.post(`${API_BASE_URL}/api/college-challenge/evaluate`, {
              challengeId: challenge.id,
          });
          const data = res.data;
          if (data.students && Array.isArray(data.students)) {
              setStudents(data.students.map(_normalizeStudent));
              setFieldErrors(data.students.map(() => ({})));
          }
          if (data.challenge) {
              setChallenge(data.challenge);
          }
          setSuccess(data.message || 'AI Evaluation completed successfully!');
          // Stay on step 2 — user must Accept before moving to scholarship
      } catch (err) {
          setError(err.response?.data?.error || 'AI Evaluation failed. Please try again.');
      } finally {
          setIsEvaluating(false);
      }
  };

  const handleAcceptEvaluation = async () => {
      if (!challenge?.id) return;
      setIsAccepting(true);
      setError(null);
      try {
          const res = await axios.post(`${API_BASE_URL}/api/college-challenge/accept-evaluation`, {
              challengeId: challenge.id,
          });
          const data = res.data;
          if (data.challenge) setChallenge(data.challenge);
          setSuccess('Evaluation accepted! You may now generate your scholarship.');
          setActiveStep(3);
      } catch (err) {
          setError(err.response?.data?.error || 'Failed to accept evaluation.');
      } finally {
          setIsAccepting(false);
      }
  };

  const handleGenerateCoupon = async () => {
      setIsGeneratingCoupon(true);
      setError(null);
      try {
          const res = await axios.post(`${API_BASE_URL}/api/college-challenge/generate-coupon`, {
              challengeId: challenge.id,
          });
          const data = res.data;
          setCouponData(data.coupon || { coupon_code: data.coupon_code, discount_percentage: challenge.scholarship_percentage });
          if (data.challenge) {
              setChallenge(data.challenge);
          } else {
              setChallenge(prev => ({ ...prev, coupon_code: data.coupon_code, coupon_generated: true }));
          }
          setSuccess('Scholarship coupon generated!');
          confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
      } catch (err) {
          setError(err.response?.data?.error || 'Failed to generate coupon.');
      } finally {
          setIsGeneratingCoupon(false);
      }
  };

  // Removed: handleOpenRetryModal — no longer needed (registration is automatic)
  // Removed: handleRetryRegistration — no longer needed (use handleRefreshStatus instead)


  if (loading) {
      return (
          <div className="college-main-layout flex items-center justify-center min-h-screen">
              <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                  <p className="text-gray-500 font-bold animate-pulse">Loading College Challenge...</p>
              </div>
          </div>
      );
  }

  if (!challenge && !loading) {
      console.log('Rendering fallback: challenge is null');
      return (
          <div className="college-main-layout flex items-center justify-center min-h-screen">
              <div className="flex flex-col items-center gap-4 text-center p-8 bg-white rounded-3xl shadow-xl max-w-md mx-auto border border-purple-100">
                  <div className="w-20 h-20 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mb-4">
                      <Trophy size={40} />
                  </div>
                  <h2 className="text-2xl font-black text-gray-900">Initializing Challenge</h2>
                  <p className="text-gray-500">We are setting up your college challenge workspace. If this takes too long, please try refreshing the page.</p>
                  <button 
                      onClick={() => window.location.reload()}
                      className="mt-4 px-8 py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg shadow-purple-200"
                  >
                      Refresh Page
                  </button>
              </div>
          </div>
      );
  }

  console.log('CollegeChallenge Rendering State:', { activeStep, challengeId: challenge?.id, status: challenge?.status, studentsCount: students.length });

  // ── Derived: interview completion eligibility ──────────────────────────────
  const INTERVIEW_COMPLETE_STATUSES = [
      'COMPLETED', 'INTERVIEW_COMPLETED', 'AI_EVALUATION_PENDING', 'AI_EVALUATION_COMPLETED',
  ];
  const allInterviewsCompleted = students.length === 5 &&
      students.every(s => INTERVIEW_COMPLETE_STATUSES.includes((s.interview_status || '').toUpperCase()));
  const allScoresGenerated = students.length === 5 &&
      students.every(s => s.ai_score !== null && s.ai_score !== undefined);
  // Backend evaluation_enabled flag is authoritative — if set, unlock evaluation regardless of frontend status strings
  const canEvaluate = challenge?.evaluation_enabled || (allInterviewsCompleted && allScoresGenerated);

  // Derived: filtered students for enterprise list view toolbar (UI-only, no business logic impact)
  const filteredStudents = students.filter(student => {
    const q = listSearchQuery.toLowerCase();
    const matchesSearch = !q ||
      (`${student.first_name || ''} ${student.last_name || ''}`).toLowerCase().includes(q) ||
      student.student_id.toLowerCase().includes(q) ||
      (student.email || '').toLowerCase().includes(q);
    const matchesDept = !listFilterDept || student.department === listFilterDept;
    const matchesYear = !listFilterYear || String(student.year) === listFilterYear;
    return matchesSearch && matchesDept && matchesYear;
  });

  return (
    <div className="college-main-layout">
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />
        <main className="college-main-content">
        <header className="mb-8 relative">
          {/* ── Per-step dynamic header ─────────────────────────────────────── */}
          {(() => {
            const stepHeaders = [
              { title: 'Nominate Students', badge: { cls: 'bg-yellow-50 text-yellow-700 border-yellow-100', dot: challenge?.status === 'Not Started' ? 'bg-yellow-500 animate-pulse' : 'bg-green-500', label: challenge?.status === 'Not Started' ? 'Registration Open' : 'Interview Scheduled' } },
              { title: 'AI Interviews in Progress', badge: { cls: 'bg-blue-50 text-blue-700 border-blue-100', dot: 'bg-blue-500 animate-pulse', label: 'Interviews Running' } },
              { title: 'Performance Evaluation', badge: { cls: 'bg-purple-50 text-purple-700 border-purple-100', dot: 'bg-purple-500', label: 'Evaluation Phase' } },
              { title: 'Scholarship Unlocked', badge: { cls: 'bg-green-50 text-green-700 border-green-100', dot: 'bg-green-500', label: 'Scholarship Earned' } },
            ];
            const h = stepHeaders[activeStep] || stepHeaders[0];
            return (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl text-white shadow-xl shadow-purple-200">
                    <Trophy size={32} />
                  </div>
                  <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight">College Challenge</h1>
                    <p className="text-gray-500 font-medium">{h.title}</p>
                  </div>
                </div>
                {challenge && (
                  <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 font-bold text-sm ${h.badge.cls}`}>
                    <div className={`w-2 h-2 rounded-full ${h.badge.dot}`}></div>
                    {h.badge.label}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Per-step info banner ─────────────────────────────────────────── */}
          {activeStep === 0 && (
            <div className="bg-white/50 backdrop-blur-sm border border-white rounded-2xl p-6 shadow-sm mb-6">
              <p className="text-gray-600 leading-relaxed">
                Nominate five eligible students to participate in the Skill Genie College Challenge. Student registrations and AI interview scheduling will be completed automatically after submission.
              </p>
            </div>
          )}
          {activeStep === 1 && (
            <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-6 shadow-sm mb-6">
              <p className="text-blue-700 leading-relaxed font-medium">
                The nominated students are currently undergoing AI-powered technical interviews. Progress will update automatically as interviews are completed.
              </p>
            </div>
          )}
          {activeStep === 2 && (
            <div className="bg-purple-50/60 border border-purple-100 rounded-2xl p-6 shadow-sm mb-6">
              <p className="text-purple-700 leading-relaxed font-medium">
                The AI evaluation has been completed successfully. Review your institution's overall performance and scholarship eligibility before proceeding.
              </p>
            </div>
          )}
          {activeStep === 3 && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-6 shadow-sm mb-6">
              <p className="text-purple-700 leading-relaxed font-medium">
                Congratulations! Your institution has qualified for an exclusive Skill Genie scholarship. Generate your one-time coupon and use it during Bootcamp enrollment or credit purchase to automatically receive the applicable discount.
              </p>
            </div>
          )}

          {activeStep === 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3 mb-8 animate-fadeIn">
              <AlertCircle className="text-amber-500" size={20} />
              <p className="text-amber-800 text-sm font-medium">
                Student details can be edited only until the Nominate Students button is clicked.
              </p>
            </div>
          )}
        </header>

        {/* Challenge Overview Card - Stepper */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-purple-50 mb-10 overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-5">
                <Trophy size={120} />
            </div>
            
            <h2 className="text-xl font-bold text-gray-800 mb-10 flex items-center gap-3">
                <div className="w-1.5 h-6 bg-purple-600 rounded-full"></div>
                Progress Timeline
            </h2>
            
            <div className="relative">
                <div className="flex justify-between items-start">
                    {steps.map((step, idx) => {
                        const isCompleted = idx < activeStep;
                        const isCurrent  = idx === activeStep;
                        const isLocked   = idx > activeStep;

                        return (
                        <div
                            key={idx}
                            className="flex flex-col items-center relative z-10 flex-1 cursor-default"
                            title={isLocked ? 'This step will be unlocked automatically when you progress.' : undefined}
                        >
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 transform ${
                                isCompleted ? 'bg-green-500 text-white' :
                                isCurrent   ? 'bg-purple-600 text-white shadow-2xl shadow-purple-300 scale-110' :
                                'bg-gray-100 text-gray-300'
                            }`}>
                                {isCompleted ? <CheckCircle2 size={24} /> :
                                 isCurrent   ? <div className="animate-pulse"><Circle size={24} fill="currentColor" fillOpacity={0.3} /></div> :
                                 <Lock size={20} />}
                            </div>
                            <div className="text-center px-2">
                                <h3 className={`text-sm font-black mb-1 ${isLocked ? 'text-gray-300' : idx <= activeStep ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {step.label}
                                </h3>
                                <p className={`text-[10px] font-medium max-w-[100px] mx-auto ${
                                    isCurrent ? 'text-purple-600' : isLocked ? 'text-gray-300' : 'text-gray-400'
                                }`}>
                                    {isLocked ? 'Locked' : step.description}
                                </p>
                            </div>
                        </div>
                        );
                    })}
                </div>
                {/* Progress Line */}
                <div className="absolute top-6 left-0 h-1 bg-gray-100 w-full -z-0 rounded-full"></div>
                <div className="absolute top-6 left-0 h-1 bg-gradient-to-r from-green-400 via-purple-500 to-purple-600 transition-all duration-1000 -z-0 rounded-full" 
                     style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}></div>
            </div>

            <div className="mt-10 pt-6 border-t border-gray-50 flex items-center justify-between">
                <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Current Step</p>
                    <p className="text-purple-700 font-bold">{steps[activeStep]?.description || 'Ready to Start'}</p>
                </div>
                {challenge?.coupon_generated && (
                     <div className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-lg shadow-purple-200 flex items-center gap-2">
                        <Trophy size={16} />
                        Scholarship Unlocked
                     </div>
                )}
            </div>
        </div>

        {challenge && activeStep === 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-purple-50 overflow-hidden animate-slideUp">
                {/* Locked state — nomination already submitted */}
                {challenge.student_nomination_completed ? (
                    <>
                        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                            <Lock size={20} className="text-gray-400" />
                            <div>
                                <h2 className="text-xl font-bold text-gray-700">Student Nomination Locked</h2>
                                <p className="text-sm text-gray-500 mt-0.5">Students have been nominated and registered. This step cannot be edited.</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3 mb-6">
                                <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                                <p className="text-sm text-amber-800 font-medium">Nomination is permanently locked. Please proceed to the AI Interviews tab to track progress.</p>
                            </div>
                            <div className="overflow-x-auto rounded-2xl border border-gray-100">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100">
                                            {['#', 'Name', 'Email', 'Phone', 'Department', 'Year', 'Primary Skills'].map(col => (
                                                <th key={col} className="px-4 py-3 text-left text-[11px] font-black text-gray-500 uppercase tracking-wider first:pl-5">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {students.map((s, idx) => (
                                            <tr key={s.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                                                <td className="pl-5 pr-3 py-3 text-sm font-bold text-gray-400">{idx + 1}</td>
                                                <td className="px-4 py-3">
                                                    <p className="text-sm font-black text-gray-900">{`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.student_name}</p>
                                                    <p className="text-[11px] text-gray-400 font-bold uppercase">{s.student_id}</p>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{s.email}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{s.phone}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{s.department || '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{s.year ? `${s.year}${['','st','nd','rd'][s.year]||'th'} Year` : '—'}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600 max-w-[160px] truncate">{s.primary_skills || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : (
                <>
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Nominate Students</h2>
                        <div className="flex items-center gap-2 mt-1">
                            <div className="text-sm font-medium text-gray-500">
                                {students.length === 5 ? '5 of 5 Students Added' : `${students.length} / 5 Completed`}
                            </div>
                            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-purple-600 transition-all duration-300"
                                    style={{ width: `${(students.length / 5) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={handleAddStudent}
                        disabled={students.length >= 5}
                        className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-purple-700 transition-all disabled:opacity-50"
                    >
                        <Plus size={18} />
                        Add Student
                    </button>
                </div>

                {students.length === 5 && (
                    <div className="px-6 py-4 bg-green-50 border-b border-green-100 flex items-center gap-3 animate-fadeIn">
                        <CheckCircle2 size={18} className="text-green-600" />
                        <p className="text-sm font-bold text-green-800">
                            All five students have been successfully nominated and are ready for interview scheduling.
                        </p>
                    </div>
                )}

                <div className="p-6">
                    {students.length === 5 && (
                        <div className="mb-8 p-6 bg-purple-50 rounded-2xl border border-purple-100 animate-fadeIn">
                            <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                                <Clipboard size={20} />
                                Ready to Nominate
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className="flex items-center gap-2 text-sm text-purple-800">
                                    <CheckCircle2 size={16} className="text-green-500" />
                                    <span>5 Students Added</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-purple-800">
                                    <CheckCircle2 size={16} className="text-green-500" />
                                    <span>All Mandatory Details Completed</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-purple-800">
                                    <CheckCircle2 size={16} className="text-green-500" />
                                    <span>No Duplicate Records Found</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-purple-800">
                                    <CheckCircle2 size={16} className="text-green-500" />
                                    <span>AI Interviews Will Be Scheduled Automatically</span>
                                </div>
                            </div>
                            <div className="flex flex-col items-center">
                                {error && <p className="mt-3 text-red-500 text-sm font-bold animate-shake">{error}</p>}
                                {success && <p className="mt-3 text-green-600 text-sm font-bold">{success}</p>}
                            </div>
                        </div>
                    )}

                    {/* ── Common Interview Schedule ──────────────────────────────────── */}
                    <div className="mb-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-200 animate-fadeIn">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-indigo-900 flex items-center gap-2">
                                <Calendar size={20} className="text-indigo-600" />
                                Interview Schedule
                            </h3>
                            <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full border border-indigo-200">
                                Applies to All Nominated Students
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Preferred Interview Date */}
                            <div className="space-y-1">
                                <div className="flex justify-between items-center ml-1">
                                    <label className="text-[10px] uppercase font-bold text-indigo-600">
                                        Preferred Interview Date <span className="text-red-500">*</span>
                                    </label>
                                    {scheduleErrors.date && (
                                        <span className="text-[9px] text-red-500 font-medium">{scheduleErrors.date}</span>
                                    )}
                                </div>
                                <div className="relative">
                                    <Calendar size={14} className="absolute left-3 top-3 text-indigo-400" />
                                    <input
                                        type="date"
                                        value={interviewDate}
                                        min={new Date().toISOString().split('T')[0]}
                                        max={new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().split('T')[0]}
                                        onChange={(e) => {
                                            setInterviewDate(e.target.value);
                                            setScheduleErrors(prev => ({ ...prev, date: undefined }));
                                        }}
                                        className={`w-full pl-9 pr-4 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all ${
                                            scheduleErrors.date ? 'border-red-400 ring-2 ring-red-300' : 'border-indigo-200'
                                        }`}
                                    />
                                </div>
                                <p className="text-[9px] text-indigo-400 ml-1">Available: Today to next 7 days.</p>
                            </div>

                            {/* Preferred Interview Time */}
                            <div className="space-y-1">
                                <div className="flex justify-between items-center ml-1">
                                    <label className="text-[10px] uppercase font-bold text-indigo-600">
                                        Preferred Interview Time <span className="text-red-500">*</span>
                                    </label>
                                    {scheduleErrors.time && (
                                        <span className="text-[9px] text-red-500 font-medium">{scheduleErrors.time}</span>
                                    )}
                                </div>
                                <div className="relative">
                                    <Clock size={14} className="absolute left-3 top-3 text-indigo-400" />
                                    <input
                                        type="time"
                                        value={interviewTime}
                                        onChange={(e) => {
                                            setInterviewTime(e.target.value);
                                            setScheduleErrors(prev => ({ ...prev, time: undefined }));
                                        }}
                                        className={`w-full pl-9 pr-4 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all ${
                                            scheduleErrors.time ? 'border-red-400 ring-2 ring-red-300' : 'border-indigo-200'
                                        }`}
                                    />
                                </div>
                            </div>

                            {/* Timezone */}
                            <div className="space-y-1">
                                <label className="text-[10px] uppercase font-bold text-indigo-600 ml-1 block">Timezone</label>
                                <div className="relative">
                                    <Clock size={14} className="absolute left-3 top-3 text-indigo-400" />
                                    <select
                                        value={interviewTimezone}
                                        onChange={(e) => setInterviewTimezone(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 bg-white border border-indigo-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none appearance-none transition-all"
                                    >
                                        <option value="Asia/Kolkata">Asia/Kolkata (IST, UTC+5:30)</option>
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">America/New_York (EST)</option>
                                        <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                                        <option value="Europe/London">Europe/London (GMT)</option>
                                        <option value="Asia/Dubai">Asia/Dubai (GST, UTC+4)</option>
                                        <option value="Asia/Singapore">Asia/Singapore (SGT, UTC+8)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-2 bg-indigo-100 rounded-xl p-3">
                            <AlertCircle size={14} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                            <p className="text-[11px] text-indigo-700 font-medium">
                                The selected interview date and time will be applied to all nominated students. Individual interview schedules are not supported.
                            </p>
                        </div>
                    </div>

                    {students.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-400">No students added yet. Start by adding your first candidate.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {students.map((student, idx) => (
                                <div key={idx} className="p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:border-purple-200 transition-all group">
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-sm">
                                                {idx + 1}
                                            </div>
                                            <h3 className="font-bold text-gray-700">Student Details</h3>
                                        </div>
                                        <button onClick={() => handleRemoveStudent(idx)} className="text-gray-300 hover:text-red-500 transition-colors">
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6">
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">First Name <span className="text-red-500">*</span></label>
                                                {fieldErrors?.[idx]?.first_name && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].first_name}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <User size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="First Name"
                                                    value={student.first_name}
                                                    onPaste={(e) => {
                                                        const text = e.clipboardData.getData('text');
                                                        if (/[^a-zA-Z\s]/.test(text)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleStudentChange(idx, 'first_name', e.target.value)}
                                                    className={`w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.first_name ? 'ring-2 ring-red-400' :
                                                        fieldErrors?.[idx]?.first_name_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                                {fieldErrors?.[idx]?.first_name_valid && !fieldErrors?.[idx]?.first_name && (
                                                    <CheckCircle2 size={14} className="absolute right-3 top-3 text-green-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Last Name <span className="text-red-500">*</span></label>
                                                {fieldErrors?.[idx]?.last_name && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].last_name}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <User size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Last Name"
                                                    value={student.last_name}
                                                    onPaste={(e) => {
                                                        const text = e.clipboardData.getData('text');
                                                        if (/[^a-zA-Z\s]/.test(text)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleStudentChange(idx, 'last_name', e.target.value)}
                                                    className={`w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.last_name ? 'ring-2 ring-red-400' :
                                                        fieldErrors?.[idx]?.last_name_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                                {fieldErrors?.[idx]?.last_name_valid && !fieldErrors?.[idx]?.last_name && (
                                                    <CheckCircle2 size={14} className="absolute right-3 top-3 text-green-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Student Roll Number</label>
                                                {fieldErrors?.[idx]?.student_id && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].student_id}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <FileText size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input 
                                                    type="text" 
                                                    placeholder="Enter Student Roll Number"
                                                    value={student.student_id}
                                                    onPaste={(e) => {
                                                        const text = e.clipboardData.getData('text');
                                                        if (/[^a-zA-Z0-9\-/]/.test(text)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleStudentChange(idx, 'student_id', e.target.value)}
                                                    className={`w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.student_id ? 'ring-2 ring-red-400' : 
                                                        fieldErrors?.[idx]?.student_id_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                                {fieldErrors?.[idx]?.student_id_valid && !fieldErrors?.[idx]?.student_id && (
                                                    <CheckCircle2 size={14} className="absolute right-3 top-3 text-green-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Email</label>
                                                {fieldErrors?.[idx]?.email && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].email}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Mail size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input 
                                                    type="email" 
                                                    placeholder="email@college.edu"
                                                    value={student.email}
                                                    onChange={(e) => handleStudentChange(idx, 'email', e.target.value)}
                                                    className={`w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.email ? 'ring-2 ring-red-400' : 
                                                        fieldErrors?.[idx]?.email_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                                {fieldErrors?.[idx]?.email_valid && !fieldErrors?.[idx]?.email && (
                                                    <CheckCircle2 size={14} className="absolute right-3 top-3 text-green-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Phone</label>
                                                {fieldErrors?.[idx]?.phone && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].phone}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Phone size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input 
                                                    type="tel" 
                                                    placeholder="10-digit Number"
                                                    value={student.phone}
                                                    onPaste={(e) => {
                                                        const text = e.clipboardData.getData('text');
                                                        if (/[^0-9]/.test(text)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleStudentChange(idx, 'phone', e.target.value)}
                                                    className={`w-full pl-9 pr-10 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.phone ? 'ring-2 ring-red-400' : 
                                                        fieldErrors?.[idx]?.phone_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                                {fieldErrors?.[idx]?.phone_valid && !fieldErrors?.[idx]?.phone && (
                                                    <CheckCircle2 size={14} className="absolute right-3 top-3 text-green-500" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Department</label>
                                                {fieldErrors?.[idx]?.department && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].department}</span>
                                                )}
                                            </div>
                                            <div className={
                                                fieldErrors?.[idx]?.department ? 'ring-2 ring-red-400 rounded-lg' : 
                                                fieldErrors?.[idx]?.department_valid ? 'ring-2 ring-green-400 rounded-lg' : ''
                                            }>
                                                <DepartmentDropdown 
                                                    value={student.department}
                                                    onChange={(val) => handleStudentChange(idx, 'department', val)}
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Year</label>
                                                {fieldErrors?.[idx]?.year && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].year}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <GraduationCap size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <select 
                                                    value={student.year}
                                                    onChange={(e) => handleStudentChange(idx, 'year', e.target.value)}
                                                    className={`w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none appearance-none transition-all ${
                                                        fieldErrors?.[idx]?.year ? 'ring-2 ring-red-400' : 
                                                        fieldErrors?.[idx]?.year_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                >
                                                    <option value="" disabled>Select Year</option>
                                                    <option value="1">1st Year</option>
                                                    <option value="2">2nd Year</option>
                                                    <option value="3">3rd Year</option>
                                                    <option value="4">4th Year</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center ml-1">
                                                <label className="text-[10px] uppercase font-bold text-gray-400">Primary Skills</label>
                                                {fieldErrors?.[idx]?.primary_skills && (
                                                    <span className="text-[9px] text-red-500 font-medium">{fieldErrors[idx].primary_skills}</span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <Search size={14} className="absolute left-3 top-3 text-gray-400" />
                                                <input 
                                                    type="text" 
                                                    placeholder="Java, Python, React, etc."
                                                    value={Array.isArray(student.primary_skills) ? student.primary_skills.join(', ') : (student.primary_skills || '')}
                                                    onChange={(e) => handleStudentChange(idx, 'primary_skills', e.target.value)}
                                                    className={`w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none transition-all ${
                                                        fieldErrors?.[idx]?.primary_skills ? 'ring-2 ring-red-400' : 
                                                        fieldErrors?.[idx]?.primary_skills_valid ? 'ring-2 ring-green-400' : ''
                                                    }`}
                                                />
                                            </div>
                                            <p className="text-[10px] text-gray-700 font-semibold ml-1">Interview questions and technical assessment topics will be generated based on the skills specified above.</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <AlertCircle size={16} className="text-purple-600" />
                        <span>Exactly 5 students required. Currently: <b className={students.length === 5 ? 'text-green-600' : 'text-purple-600'}>{students.length}/5</b></span>
                    </div>
                    <button
                        onClick={handleNominateStudents}
                        disabled={isNominating || loading || students.length !== 5}
                        className="bg-purple-600 text-white px-10 py-3 rounded-xl font-bold hover:bg-purple-700 transition-all shadow-lg shadow-purple-100 disabled:opacity-50 flex items-center gap-2"
                    >
                        {isNominating ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Scheduling AI Interviews...
                            </>
                        ) : (
                            'Nominate Students'
                        )}
                    </button>
                </div>
            </>
            )}
            </div>
        )}

        {challenge && activeStep === 1 && (() => {
            // ── Counts ────────────────────────────────────────────────────────
            const regOk = students.filter(s =>
                s.exihire_registration_status === 'SUCCESS' || s.exihire_registration_status === 'ALREADY_EXISTS'
            ).length;
            const regFailed = students.filter(s => s.exihire_registration_status === 'FAILED').length;

            const _IV_COMPLETE = ['COMPLETED', 'INTERVIEW_COMPLETED', 'AI_EVALUATION_PENDING', 'AI_EVALUATION_COMPLETED'];
            const ivCompleted  = students.filter(s => _IV_COMPLETE.includes((s.interview_status || '').toUpperCase())).length;
            const ivScheduled  = students.filter(s => ['SCHEDULED', 'INTERVIEW_SCHEDULED'].includes((s.interview_status || '').toUpperCase())).length;
            const ivInProgress = students.filter(s => ['STARTED', 'IN_PROGRESS', 'INTERVIEW_STARTED', 'INTERVIEW_IN_PROGRESS'].includes((s.interview_status || '').toUpperCase())).length;
            const ivFailed     = students.filter(s => ['SCHEDULE_FAILED', 'FAILED'].includes((s.interview_status || '').toUpperCase())).length;

            const uniqueDepts = [...new Set(students.map(s => s.department).filter(Boolean))].sort();

            // ── Registration badge ─────────────────────────────────────────────
            const getRegBadge = (student) => {
                const status = student.exihire_registration_status || 'PENDING';
                if (status === 'SUCCESS')        return { cls: 'bg-green-50 text-green-700 border-green-200',   label: 'Registered' };
                if (status === 'ALREADY_EXISTS') return { cls: 'bg-blue-50 text-blue-700 border-blue-200',     label: 'Already Registered' };
                if (status === 'FAILED')         return { cls: 'bg-red-50 text-red-700 border-red-200',        label: 'Failed' };
                return                                  { cls: 'bg-gray-50 text-gray-500 border-gray-200',     label: status === 'PENDING' ? 'Pending' : status };
            };

            // ── Interview status badge ─────────────────────────────────────────
            const getInterviewBadge = (student) => {
                const raw = (student.interview_status || 'NOT_STARTED').toUpperCase();
                if (['NOT_STARTED', 'NOT SCHEDULED'].includes(raw))
                    return { cls: 'bg-gray-50 text-gray-500 border-gray-200',         label: 'Not Scheduled',           dot: 'bg-gray-400' };
                if (['SCHEDULED', 'INTERVIEW_SCHEDULED'].includes(raw))
                    return { cls: 'bg-blue-50 text-blue-700 border-blue-200',          label: 'Scheduled',               dot: 'bg-blue-500' };
                if (['LINK_SENT', 'INTERVIEW_LINK_SENT'].includes(raw))
                    return { cls: 'bg-cyan-50 text-cyan-700 border-cyan-200',          label: 'Link Sent',               dot: 'bg-cyan-500' };
                if (['STARTED', 'INTERVIEW_STARTED'].includes(raw))
                    return { cls: 'bg-orange-50 text-orange-700 border-orange-200',    label: 'Interview Started',       dot: 'bg-orange-500' };
                if (['IN_PROGRESS', 'INTERVIEW_IN_PROGRESS'].includes(raw))
                    return { cls: 'bg-orange-50 text-orange-700 border-orange-200',    label: 'In Progress',             dot: 'bg-orange-500' };
                if (['COMPLETED', 'INTERVIEW_COMPLETED'].includes(raw))
                    return { cls: 'bg-green-50 text-green-700 border-green-200',       label: 'Interview Completed',     dot: 'bg-green-500' };
                if (['AI_EVALUATION_PENDING', 'EVALUATION_PENDING'].includes(raw))
                    return { cls: 'bg-purple-50 text-purple-700 border-purple-200',    label: 'AI Eval Pending',         dot: 'bg-purple-500' };
                if (['AI_EVALUATION_COMPLETED', 'EVALUATION_COMPLETED'].includes(raw))
                    return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'AI Eval Completed',       dot: 'bg-emerald-600' };
                if (['FAILED', 'SCHEDULE_FAILED'].includes(raw))
                    return { cls: 'bg-red-50 text-red-700 border-red-200',             label: 'Failed',                  dot: 'bg-red-500' };
                return     { cls: 'bg-gray-50 text-gray-500 border-gray-200',          label: student.interview_status,  dot: 'bg-gray-400' };
            };

            // ── Score badge ────────────────────────────────────────────────────
            const getScoreBadge = (score) => {
                if (score === null || score === undefined)
                    return { cls: 'bg-gray-100 text-gray-400', label: '—', tooltip: 'Interview not yet completed.' };
                const n = Number(score);
                if (n > 80) return { cls: 'bg-green-100 text-green-800',  label: `${n}%`, tooltip: 'Excellent performance. Eligible for scholarship consideration.' };
                if (n >= 60) return { cls: 'bg-amber-100 text-amber-800', label: `${n}%`, tooltip: 'Good performance. Meets expectations.' };
                return             { cls: 'bg-red-100 text-red-700',      label: `${n}%`, tooltip: 'Performance below the recommended threshold.' };
            };

            // ── Client-side filter + sort ──────────────────────────────────────
            const tableStudents = students
                .filter(s => {
                    const fullName = (`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.student_name || '').toLowerCase();
                    const matchSearch = !listSearchQuery ||
                        fullName.includes(listSearchQuery.toLowerCase()) ||
                        (s.email || '').toLowerCase().includes(listSearchQuery.toLowerCase()) ||
                        (s.student_id || '').toLowerCase().includes(listSearchQuery.toLowerCase());
                    const matchDept   = !listFilterDept || s.department === listFilterDept;
                    const matchYear   = !listFilterYear || String(s.year) === listFilterYear;
                    const matchStatus = !listFilterStatus ||
                        (listFilterStatus === 'registered'  && (s.exihire_registration_status === 'SUCCESS' || s.exihire_registration_status === 'ALREADY_EXISTS')) ||
                        (listFilterStatus === 'reg_failed'  && s.exihire_registration_status === 'FAILED') ||
                        (listFilterStatus === 'iv_done'     && INTERVIEW_COMPLETE_STATUSES.includes(s.interview_status)) ||
                        (listFilterStatus === 'iv_scheduled' && s.interview_status === 'SCHEDULED') ||
                        (listFilterStatus === 'iv_failed'   && ['SCHEDULE_FAILED', 'FAILED'].includes(s.interview_status));
                    return matchSearch && matchDept && matchYear && matchStatus;
                })
                .sort((a, b) => {
                    if (listSortBy === 'score') {
                        const sa = a.ai_score ?? -1;
                        const sb = b.ai_score ?? -1;
                        return sb - sa; // highest first
                    }
                    if (listSortBy === 'iv_status') {
                        const rank = s => INTERVIEW_COMPLETE_STATUSES.includes(s.interview_status) ? 0 :
                            ['SCHEDULE_FAILED','FAILED'].includes(s.interview_status) ? 3 :
                            s.interview_status === 'SCHEDULED' ? 1 : 2;
                        return rank(a) - rank(b);
                    }
                    const na = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.student_name || '').toLowerCase();
                    const nb = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.student_name || '').toLowerCase();
                    return na.localeCompare(nb);
                });

            return (
             <div className="space-y-6 animate-slideUp">

                {/* ── Top Banner ────────────────────────────────────────────────── */}
                {allInterviewsCompleted ? (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm">
                        <div className="w-14 h-14 bg-green-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-green-200 flex-shrink-0">
                            <CheckCircle2 size={28} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-black text-green-900 mb-0.5">All Interviews Completed</h2>
                            <p className="text-green-700 font-medium text-sm">
                                All {students.length} students have completed their AI interviews.
                                {canEvaluate ? ' AI Evaluation is now available.' : ' Waiting for scores to be generated.'}
                            </p>
                        </div>
                        {canEvaluate && (
                            <button
                                onClick={() => setActiveStep(2)}
                                className="flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-green-700 transition-all shadow-md flex-shrink-0"
                            >
                                Proceed to AI Evaluation
                                <ChevronDown size={16} className="-rotate-90" />
                            </button>
                        )}
                    </div>
                ) : ivFailed > 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm">
                        <div className="w-14 h-14 bg-amber-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-amber-200 flex-shrink-0">
                            <AlertCircle size={28} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-black text-amber-900 mb-0.5">Attention Required</h2>
                            <p className="text-amber-700 font-medium text-sm">
                                {ivFailed} student{ivFailed > 1 ? 's' : ''} have scheduling issues.
                                {ivScheduled > 0 && ` ${ivScheduled} interview${ivScheduled > 1 ? 's' : ''} are scheduled.`}
                            </p>
                        </div>
                        <button onClick={handleRefreshStatus} disabled={isRefreshing}
                            className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-amber-700 disabled:opacity-50 flex-shrink-0">
                            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                ) : ivCompleted > 0 ? (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm">
                        <div className="w-14 h-14 bg-purple-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-purple-200 flex-shrink-0">
                            <CheckCircle2 size={28} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-black text-purple-900 mb-0.5">Interviews In Progress</h2>
                            <p className="text-purple-700 font-medium text-sm">
                                {ivCompleted} of {students.length} interviews completed. Auto-refreshing every 30 seconds.
                            </p>
                        </div>
                        <button onClick={handleRefreshStatus} disabled={isRefreshing}
                            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-purple-700 disabled:opacity-50 flex-shrink-0">
                            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh Now'}
                        </button>
                    </div>
                ) : (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm">
                        <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 flex-shrink-0">
                            <Trophy size={28} />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-xl font-black text-blue-900 mb-0.5">Students Registered · Interviews Pending</h2>
                            <p className="text-blue-700 font-medium text-sm">
                                {regOk} of {students.length} students registered on Exihire. Interviews will be scheduled shortly.
                            </p>
                        </div>
                        <button onClick={handleRefreshStatus} disabled={isRefreshing}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50 flex-shrink-0">
                            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                )}

                {/* ── Summary Stats ─────────────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                        { label: 'Total',        value: students.length,  bg: 'bg-purple-50', text: 'text-purple-900', border: 'border-purple-100', icon: '👥' },
                        { label: 'Registered',   value: regOk,            bg: 'bg-green-50',  text: 'text-green-900',  border: 'border-green-100',  icon: '✅' },
                        { label: 'Reg Failed',   value: regFailed,        bg: 'bg-red-50',    text: 'text-red-900',    border: 'border-red-100',    icon: '❌' },
                        { label: 'Scheduled',           value: ivScheduled, bg: 'bg-blue-50',    text: 'text-blue-900',    border: 'border-blue-100',    icon: '📅' },
                        { label: 'Interview Completed', value: ivCompleted, bg: 'bg-emerald-50', text: 'text-emerald-900', border: 'border-emerald-100',  icon: '🎯' },
                        { label: 'Interview Failed',    value: ivFailed,    bg: 'bg-orange-50',  text: 'text-orange-900',  border: 'border-orange-100',   icon: '⚠️' },
                    ].map(({ label, value, bg, text, border, icon }) => (
                        <div key={label} className={`bg-white rounded-xl border ${border} p-3 flex items-center gap-2.5 shadow-sm`}>
                            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center text-base flex-shrink-0`}>{icon}</div>
                            <div>
                                <p className={`text-xl font-black ${text}`}>{value}</p>
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider leading-tight">{label}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Toolbar ───────────────────────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center shadow-sm">
                    <div className="relative flex-1 min-w-44">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input type="text" placeholder="Search name, email, ID…" value={listSearchQuery}
                            onChange={e => setListSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-8 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition-all" />
                        {listSearchQuery && (
                            <button onClick={() => setListSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={13} /></button>
                        )}
                    </div>
                    <select value={listFilterDept} onChange={e => setListFilterDept(e.target.value)}
                        className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option value="">All Depts</option>
                        {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={listFilterYear} onChange={e => setListFilterYear(e.target.value)}
                        className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option value="">All Years</option>
                        {['1','2','3','4'].map(y => <option key={y} value={y}>{y}{'th'} Year</option>)}
                    </select>
                    <select value={listFilterStatus} onChange={e => setListFilterStatus(e.target.value)}
                        className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option value="">All Statuses</option>
                        <option value="registered">Registered</option>
                        <option value="reg_failed">Reg Failed</option>
                        <option value="iv_scheduled">IV Scheduled</option>
                        <option value="iv_done">IV Completed</option>
                        <option value="iv_failed">IV Failed</option>
                    </select>
                    <select value={listSortBy} onChange={e => setListSortBy(e.target.value)}
                        className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option value="name">Sort: Name A→Z</option>
                        <option value="score">Sort: Score ↓</option>
                        <option value="iv_status">Sort: IV Status</option>
                    </select>
                    {(listSearchQuery || listFilterDept || listFilterYear || listFilterStatus) && (
                        <button onClick={() => { setListSearchQuery(''); setListFilterDept(''); setListFilterYear(''); setListFilterStatus(''); }}
                            className="flex items-center gap-1 text-xs font-bold text-purple-600 hover:text-purple-800 px-2 py-1 rounded-lg hover:bg-purple-50">
                            <X size={12} /> Clear
                        </button>
                    )}
                    <span className="ml-auto text-xs font-bold text-gray-400 whitespace-nowrap">{tableStudents.length} / {students.length}</span>
                </div>

                {/* ── Student Progress Table ─────────────────────────────────────── */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse" role="grid">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    {['#', 'Student', 'Email', 'Dept / Year', 'Primary Skills', 'Registration', 'Interview Status', 'Interview Score', 'Candidate ID', 'Actions'].map(col => (
                                        <th key={col} scope="col"
                                            className="px-4 py-3 text-left text-[11px] font-black text-gray-500 uppercase tracking-wider whitespace-nowrap first:pl-5">
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {tableStudents.length === 0 ? (
                                    <tr><td colSpan={10} className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center"><Search size={28} className="text-gray-400" /></div>
                                            <p className="font-black text-gray-600">No Students Found</p>
                                            <p className="text-sm text-gray-400">Try adjusting your search or filters.</p>
                                        </div>
                                    </td></tr>
                                ) : tableStudents.map((student, rowIdx) => {
                                    const regBadge = getRegBadge(student);
                                    const ivBadge  = getInterviewBadge(student);
                                    const scoreBadge = getScoreBadge(student.ai_score);
                                    const isEvenRow = rowIdx % 2 === 0;
                                    return (
                                        <tr key={student.id || rowIdx}
                                            className={`transition-colors hover:bg-purple-50/30 ${isEvenRow ? 'bg-white' : 'bg-gray-50/40'}`}>

                                            {/* # */}
                                            <td className="pl-5 pr-3 py-3.5 text-sm font-bold text-gray-400 w-10">{rowIdx + 1}</td>

                                            {/* Student */}
                                            <td className="px-4 py-3.5 min-w-[180px]">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-black text-sm flex-shrink-0">
                                                        {(student.first_name || student.student_name || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-black text-gray-900 truncate max-w-[150px]">
                                                            {`${student.first_name||''} ${student.last_name||''}`.trim() || student.student_name}
                                                        </p>
                                                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{student.student_id}</p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Email */}
                                            <td className="px-4 py-3.5 min-w-[180px]">
                                                <p className="text-sm text-gray-600 truncate max-w-[200px]">{student.email}</p>
                                                <p className="text-[11px] text-gray-400">{student.phone || '—'}</p>
                                            </td>

                                            {/* Dept / Year */}
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <p className="text-sm font-semibold text-gray-700">{student.department || '—'}</p>
                                                <p className="text-[11px] text-gray-400">
                                                    {student.year ? `${student.year}${['','st','nd','rd'][student.year]||'th'} Year` : '—'}
                                                </p>
                                            </td>

                                            {/* Skills */}
                                            <td className="px-4 py-3.5 min-w-[150px]">
                                                <p className="text-sm text-gray-600 truncate max-w-[150px]" title={student.primary_skills}>
                                                    {student.primary_skills || '—'}
                                                </p>
                                            </td>

                                            {/* Registration Status */}
                                            <td className="px-4 py-3.5 min-w-[140px]">
                                                <div className="flex flex-col gap-1">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black border w-fit ${regBadge.cls}`}>
                                                        {regBadge.label}
                                                    </span>
                                                    {student.exihire_registration_status === 'FAILED' && student.registration_error_message && (
                                                        <p className="text-[10px] text-red-500 font-medium truncate max-w-[160px]" title={student.registration_error_message}>
                                                            {student.registration_error_message}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Interview Status */}
                                            <td className="px-4 py-3.5 min-w-[160px]">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ivBadge.dot}`}></span>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black border w-fit ${ivBadge.cls}`}>
                                                        {ivBadge.label}
                                                    </span>
                                                </div>
                                                {student.interview_link && (
                                                    <a href={student.interview_link} target="_blank" rel="noreferrer"
                                                        className="mt-1 text-[10px] text-blue-600 hover:underline font-medium block truncate max-w-[150px]">
                                                        Interview Link ↗
                                                    </a>
                                                )}
                                            </td>

                                            {/* Interview Score */}
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-black ${scoreBadge.cls}`}
                                                    title={scoreBadge.tooltip}
                                                >
                                                    {scoreBadge.label}
                                                </span>
                                            </td>

                                            {/* Candidate ID */}
                                            <td className="px-4 py-3.5 min-w-[140px]">
                                                {student.exihire_candidate_id ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-xs font-mono text-gray-600 truncate max-w-[110px]" title={student.exihire_candidate_id}>
                                                            {student.exihire_candidate_id}
                                                        </span>
                                                        <button onClick={() => navigator.clipboard.writeText(student.exihire_candidate_id)}
                                                            className="text-gray-400 hover:text-purple-600 flex-shrink-0" title="Copy ID">
                                                            <Clipboard size={11} />
                                                        </button>
                                                    </div>
                                                ) : <span className="text-xs text-gray-400">—</span>}
                                            </td>

                                            {/* Actions */}
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                {student.interview_link ? (
                                                    <a href={student.interview_link} target="_blank" rel="noreferrer"
                                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-lg transition-colors border border-purple-100">
                                                        Open Link
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-gray-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Table footer */}
                    <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                        <p className="text-xs text-gray-400">
                            {isRefreshing ? (
                                <span className="flex items-center gap-1.5"><RefreshCw size={11} className="animate-spin" /> Refreshing…</span>
                            ) : (
                                'Auto-refreshes every 30 seconds while interviews are in progress.'
                            )}
                        </p>
                    </div>
                </div>

                {/* ── CTA: Proceed to AI Evaluation (shown when all done) ──────────── */}
                {canEvaluate && (
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-4 shadow-xl shadow-purple-100">
                        <div className="flex-1 text-white">
                            <h3 className="font-black text-lg mb-1">All Interviews Complete — Ready for AI Evaluation</h3>
                            <p className="text-purple-200 text-sm">Click below to generate AI-powered performance analysis, rankings, and scholarship eligibility.</p>
                        </div>
                        <button
                            onClick={() => setActiveStep(2)}
                            className="flex items-center gap-2 bg-white text-purple-700 px-6 py-3 rounded-xl font-black hover:bg-purple-50 transition-all shadow-lg flex-shrink-0"
                        >
                            Proceed to AI Evaluation
                            <ChevronDown size={16} className="-rotate-90" />
                        </button>
                    </div>
                )}
             </div>
            );
        })()}

        {activeStep === 2 && (
            <div className="space-y-8 animate-slideUp">
                {/* Pre-evaluation: run AI analysis */}
                {!challenge?.evaluation_completed && (
                    <>
                        {/* locked state check */}
                        {!canEvaluate && (
                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl p-10 flex flex-col items-center text-center gap-4">
                                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center">
                                    <Lock size={36} className="text-gray-300" />
                                </div>
                                <h2 className="text-2xl font-black text-gray-400">AI Evaluation Locked</h2>
                                <p className="text-gray-400 font-medium max-w-md">
                                    Waiting for all 5 nominated students to complete their AI Interviews.
                                </p>
                                <div className="flex gap-3 mt-2">
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border ${allInterviewsCompleted ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                                        {allInterviewsCompleted ? <CheckCircle2 size={16}/> : <Circle size={16}/>}
                                        All interviews completed
                                    </div>
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border ${allScoresGenerated ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                                        {allScoresGenerated ? <CheckCircle2 size={16}/> : <Circle size={16}/>}
                                        Scores available
                                    </div>
                                </div>
                            </div>
                        )}
                        {canEvaluate && (
                            <>
                                <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-8 text-white shadow-2xl shadow-purple-200">
                                    <h2 className="text-2xl font-black mb-1">All Interviews Complete — Ready for AI Evaluation</h2>
                                    <p className="text-purple-200">Click Evaluate Performance to generate rankings, average score, and scholarship eligibility.</p>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                    {students.map((student, idx) => {
                                        const score = student.ai_score;
                                        const name = `${student.first_name||''} ${student.last_name||''}`.trim() || student.student_name;
                                        const colorCls = score === null || score === undefined ? 'bg-gray-50 border-gray-200 text-gray-500' :
                                            Number(score) > 80 ? 'bg-green-50 border-green-200 text-green-800' :
                                            Number(score) >= 60 ? 'bg-amber-50 border-amber-200 text-amber-800' :
                                            'bg-red-50 border-red-200 text-red-800';
                                        return (
                                            <div key={idx} className={`rounded-2xl border p-4 flex flex-col items-center text-center gap-2 ${colorCls}`}>
                                                <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center font-black text-sm">{name.charAt(0).toUpperCase()}</div>
                                                <p className="text-xs font-black truncate w-full">{name}</p>
                                                <p className="text-2xl font-black">{score !== null && score !== undefined ? `${score}%` : '—'}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="bg-white rounded-3xl p-8 border border-purple-100 shadow-sm text-center">
                                    <h3 className="text-xl font-black text-gray-900 mb-2">Evaluate Performance</h3>
                                    <p className="text-gray-500 mb-6 text-sm max-w-lg mx-auto">AI-powered analysis across all 5 interviews — rankings, performance breakdowns, and scholarship eligibility.</p>
                                    <button onClick={handleEvaluatePerformance} disabled={isEvaluating}
                                        className="bg-purple-600 text-white px-12 py-4 rounded-2xl font-black text-lg hover:bg-purple-700 transition-all shadow-xl shadow-purple-200 disabled:opacity-60 flex items-center gap-3 mx-auto">
                                        {isEvaluating ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Evaluating…</> : <><Trophy size={22}/>Evaluate Performance</>}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}

                {/* Post-evaluation results — before acceptance */}
                {challenge?.evaluation_completed && !challenge?.evaluation_accepted && (() => {
                    const studentScores = students
                        .map(s => Number(s.ai_score !== null && s.ai_score !== undefined ? s.ai_score : s.score))
                        .filter(s => !isNaN(s) && s > 0);
                    const calcAvg = studentScores.length > 0 ? studentScores.reduce((a, b) => a + b, 0) / studentScores.length : 0;
                    const calcMax = studentScores.length > 0 ? Math.max(...studentScores) : 0;

                    const avgScore = Number(challenge?.average_score || challenge?.highest_score || calcAvg || calcMax || 0);
                    const scholarshipPct = challenge.scholarship_percentage || 0;
                    const originalPrice = 9999;
                    const discountAmount = Math.round(originalPrice * scholarshipPct / 100);
                    const finalPrice = originalPrice - discountAmount;
                    const perfLabel = avgScore > 80 ? 'Excellent' : avgScore >= 60 ? 'Good' : avgScore >= 40 ? 'Average' : 'Needs Improvement';
                    const perfColor = avgScore > 80 ? 'text-green-700 bg-green-50 border-green-200' : avgScore >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-red-700 bg-red-50 border-red-200';
                    return (
                        <>
                            {/* Header */}
                            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-8 text-white shadow-2xl shadow-green-200">
                                <div className="flex items-center gap-4 mb-4"><CheckCircle2 size={40}/><h2 className="text-2xl font-black">AI Evaluation Complete</h2></div>
                                <p className="text-green-100">Review your college's performance below. Accept and proceed to generate your scholarship coupon.</p>
                            </div>

                            {/* Average Score */}
                            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm text-center">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Overall College Performance</p>
                                <div className="text-7xl font-black text-gray-900 mb-2">{avgScore.toFixed(1)}%</div>
                                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-black border ${perfColor}`}>
                                    <Trophy size={14}/>{perfLabel}
                                </div>
                                <p className="mt-4 text-xs text-gray-400 max-w-sm mx-auto">
                                    The average interview score is calculated using the performance of all five nominated students. Based on the average score, the college becomes eligible for a scholarship discount applicable to Skill Genie Bootcamp enrollment.
                                </p>
                            </div>

                            {/* Student Scores */}
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                {students.map((student, idx) => {
                                    const score = student.ai_score;
                                    const name = `${student.first_name||''} ${student.last_name||''}`.trim() || student.student_name;
                                    const rank = student.rank;
                                    const colorCls = score === null || score === undefined ? 'bg-gray-50 border-gray-200 text-gray-500' :
                                        Number(score) > 80 ? 'bg-green-50 border-green-200 text-green-800' :
                                        Number(score) >= 60 ? 'bg-amber-50 border-amber-200 text-amber-800' :
                                        'bg-red-50 border-red-200 text-red-800';
                                    return (
                                        <div key={idx} className={`rounded-2xl border p-4 flex flex-col items-center text-center gap-1 ${colorCls} relative`}>
                                            {rank === 1 && <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-[10px] font-black text-gray-900">🏆</div>}
                                            <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center font-black text-sm">{name.charAt(0).toUpperCase()}</div>
                                            <p className="text-xs font-black truncate w-full">{name}</p>
                                            <p className="text-2xl font-black">{score !== null && score !== undefined ? `${score}%` : '—'}</p>
                                            {rank && <p className="text-[9px] font-bold opacity-60">RANK #{rank}</p>}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Scholarship Benefits */}
                            <div className="bg-white rounded-3xl p-8 border border-purple-100 shadow-sm">
                                <h3 className="text-xl font-black text-gray-900 mb-2 flex items-center gap-2"><GraduationCap size={22} className="text-purple-600"/>Scholarship Benefits</h3>
                                <p className="text-sm text-gray-500 mb-6">Based on your college's overall AI interview performance, every eligible student enrolling through this scholarship program will receive the discounted fee shown below.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
                                        <p className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-1">Scholarship Earned</p>
                                        <div className="text-5xl font-black text-purple-700 mb-1">{scholarshipPct}%</div>
                                        <p className="text-sm text-purple-600 font-medium">Discount on Skill Genie Bootcamp enrollment</p>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                            <span className="text-gray-500 text-sm">Original Skill Genie Bootcamp Fee</span>
                                            <span className="font-bold text-gray-900 line-through">₹{originalPrice.toLocaleString('en-IN')} per student</span>
                                        </div>
                                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                            <span className="text-gray-500 text-sm">Scholarship Benefit ({scholarshipPct}%)</span>
                                            <span className="font-bold text-green-600">− ₹{discountAmount.toLocaleString('en-IN')} per student</span>
                                        </div>
                                        <div className="flex justify-between items-center py-3 bg-purple-50 px-3 rounded-xl">
                                            <span className="text-gray-800 text-sm font-bold">Final Enrollment Fee</span>
                                            <span className="font-black text-purple-700 text-lg">₹{finalPrice.toLocaleString('en-IN')} per student</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                                    <p className="text-sm text-indigo-700 leading-relaxed">
                                        This scholarship benefit is applicable <strong>per student</strong>. During Skill Genie Bootcamp enrollment or credit purchase, the discount will be automatically applied to the total payable amount based on the number of eligible students when this scholarship coupon is redeemed.
                                    </p>
                                </div>
                            </div>

                            {/* Accept & Next */}
                            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
                                <div>
                                    <h3 className="text-lg font-black text-gray-900 mb-1">Ready to claim your scholarship?</h3>
                                    <p className="text-gray-500 text-sm">Accepting locks the evaluation permanently. You will be redirected to generate your coupon code.</p>
                                </div>
                                <div className="flex gap-4 items-center">
                                    <button disabled className="px-6 py-3 rounded-2xl font-bold text-gray-300 bg-gray-100 cursor-not-allowed text-sm">← Back</button>
                                    <button onClick={handleAcceptEvaluation} disabled={isAccepting}
                                        className="bg-purple-600 text-white px-10 py-4 rounded-2xl font-black text-base hover:bg-purple-700 transition-all shadow-xl shadow-purple-200 disabled:opacity-60 flex items-center gap-3">
                                        {isAccepting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Accepting…</> : <>Accept & Next →</>}
                                    </button>
                                </div>
                            </div>
                        </>
                    );
                })()}

                {/* After acceptance — locked */}
                {challenge?.evaluation_accepted && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-3xl p-10 flex flex-col items-center text-center gap-4">
                        <CheckCircle2 size={48} className="text-green-500"/>
                        <h2 className="text-2xl font-black text-green-800">Evaluation Accepted & Locked</h2>
                        <p className="text-green-600 font-medium">Your evaluation has been permanently accepted. Proceed to generate your scholarship coupon.</p>
                        <button onClick={() => setActiveStep(3)} className="bg-purple-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-purple-700 transition-all shadow-lg shadow-purple-200">
                            Go to Scholarship →
                        </button>
                    </div>
                )}
            </div>
        )}

        {activeStep === 3 && (
            <div className="space-y-6 animate-slideUp">
                {/* Scholarship Hero Banner */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 pointer-events-none"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                                <GraduationCap size={26} />
                            </div>
                            <div>
                                <p className="text-purple-200 text-xs font-bold uppercase tracking-widest">Skill Genie College Challenge</p>
                                <h2 className="text-2xl font-black">🎓 Scholarship Qualification Achieved</h2>
                            </div>
                        </div>
                        <p className="text-purple-100 mb-6 text-sm max-w-2xl leading-relaxed">
                            Your institution has successfully completed the College Challenge and qualified for an exclusive Skill Genie scholarship based on the collective performance of the nominated students.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
                                <p className="text-purple-200 text-xs font-bold uppercase tracking-widest mb-1">Average Interview Score</p>
                                <div className="text-4xl font-black">
                                    {(() => {
                                        const studentScores = students
                                            .map(s => Number(s.ai_score !== null && s.ai_score !== undefined ? s.ai_score : s.score))
                                            .filter(s => !isNaN(s) && s > 0);
                                        const calcAvg = studentScores.length > 0 ? studentScores.reduce((a, b) => a + b, 0) / studentScores.length : 0;
                                        return Number(challenge?.average_score || challenge?.highest_score || calcAvg || 0).toFixed(1);
                                    })()}%
                                </div>
                            </div>
                            <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
                                <p className="text-purple-200 text-xs font-bold uppercase tracking-widest mb-1">Scholarship Earned</p>
                                <div className="text-4xl font-black text-yellow-400">{challenge.scholarship_percentage}%</div>
                                <p className="text-purple-200 text-xs mt-1">Scholarship Discount</p>
                            </div>
                            <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
                                <p className="text-purple-200 text-xs font-bold uppercase tracking-widest mb-1">Eligibility</p>
                                <div className="text-base font-black leading-snug">Qualified for Skill Genie Bootcamp</div>
                                <p className="text-purple-200 text-xs mt-1">Scholarship applicable per student</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scholarship Benefits Summary */}
                {(() => {
                    const scholarshipPct = challenge.scholarship_percentage || 0;
                    const originalPrice = 9999;
                    const discountAmount = Math.round(originalPrice * scholarshipPct / 100);
                    const finalPrice = originalPrice - discountAmount;
                    return (
                        <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm">
                            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2"><GraduationCap size={18} className="text-purple-600"/>Scholarship Benefits</h3>
                            <p className="text-sm text-gray-500 mb-5">Based on your college's overall AI interview performance, every eligible student enrolling through this scholarship program will receive the discounted fee shown below.</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[
                                    { label: 'Original Bootcamp Fee', value: `₹${originalPrice.toLocaleString('en-IN')}`, sub: 'per student', color: 'text-gray-400 line-through' },
                                    { label: 'Scholarship', value: `${scholarshipPct}%`, sub: 'discount earned', color: 'text-green-600' },
                                    { label: 'Discount Benefit', value: `₹${discountAmount.toLocaleString('en-IN')}`, sub: 'per student', color: 'text-green-600' },
                                    { label: 'Final Enrollment Fee', value: `₹${finalPrice.toLocaleString('en-IN')}`, sub: 'per student', color: 'text-purple-700' },
                                ].map(({ label, value, sub, color }) => (
                                    <div key={label} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 text-center">
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">{label}</p>
                                        <p className={`text-2xl font-black ${color}`}>{value}</p>
                                        <p className="text-[10px] text-gray-400 mt-1">{sub}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                                <p className="text-sm text-indigo-700 leading-relaxed">
                                    This scholarship benefit is applicable <strong>per student</strong>. During Skill Genie Bootcamp enrollment or credit purchase, the discount will be automatically applied to the total payable amount based on the number of eligible students when this scholarship coupon is redeemed.
                                </p>
                            </div>
                        </div>
                    );
                })()}

                {/* Coupon Generation or Display */}
                {!challenge?.coupon_generated ? (
                    <div className="bg-white rounded-3xl p-8 border border-purple-100 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div>
                                <h3 className="text-xl font-black text-gray-900 mb-2">Generate Scholarship Coupon</h3>
                                <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                                    Generate your institution's exclusive one-time scholarship coupon. This coupon can be applied during Skill Genie Bootcamp enrollment or credit purchase, and the eligible scholarship amount will automatically be deducted from the final payable amount.
                                </p>
                                <button onClick={handleGenerateCoupon} disabled={isGeneratingCoupon}
                                    className="bg-purple-600 text-white px-10 py-4 rounded-2xl font-black text-base hover:bg-purple-700 transition-all shadow-xl shadow-purple-200 disabled:opacity-60 flex items-center gap-3">
                                    {isGeneratingCoupon ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Generating…</> : <><GraduationCap size={20}/>Generate Scholarship Coupon</>}
                                </button>
                            </div>
                            <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100 space-y-2.5">
                                {[
                                    'One-time coupon',
                                    'Mapped exclusively to your college',
                                    'Auto-validates during payment',
                                    'Scholarship applied automatically',
                                    'Cannot be regenerated once issued',
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2.5">
                                        <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                                            <CheckCircle2 size={12} />
                                        </div>
                                        <p className="text-sm font-medium text-purple-800">{item}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Coupon Card */}
                        <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-gray-800 flex items-center gap-2"><FileText size={18} className="text-purple-600"/>Scholarship Coupon</h3>
                                <div className="flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full text-xs font-black">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>ACTIVE
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100 mb-4">
                                <p className="text-[10px] font-bold text-purple-400 uppercase tracking-widest mb-2">One-Time Redemption Code</p>
                                <div className="flex items-center justify-between gap-4">
                                    <p className="text-2xl font-mono font-black text-purple-700 tracking-wider">{challenge.coupon_code || couponData?.coupon_code}</p>
                                    <button onClick={() => { navigator.clipboard.writeText(challenge.coupon_code || couponData?.coupon_code || ''); setSuccess('Coupon code copied!'); }}
                                        className="p-3 bg-white shadow-sm rounded-xl text-purple-600 hover:text-purple-800 border border-purple-100 transition-all flex-shrink-0">
                                        <Clipboard size={20}/>
                                    </button>
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <span className="bg-yellow-100 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-lg text-[10px] font-black">ONE-TIME USE</span>
                                    <span className="bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-lg text-[10px] font-black">EXPIRES IN 90 DAYS</span>
                                </div>
                            </div>
                            <div className="text-xs text-gray-400 space-y-1">
                                <p>• Generated: {new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</p>
                                <p>• Expires: {new Date(new Date().setDate(new Date().getDate()+90)).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</p>
                            </div>
                        </div>

                        {/* How Your Scholarship Works */}
                        <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm flex flex-col justify-between">
                            <div>
                                <h3 className="font-bold text-gray-800 mb-1">How Your Scholarship Works</h3>
                                <p className="text-xs text-gray-400 mb-4">Your coupon is ready to use during Skill Genie Bootcamp enrollment or credit purchase.</p>
                                <ul className="text-sm text-gray-600 space-y-3">
                                    {[
                                        'The scholarship benefit is applicable per student.',
                                        'The generated coupon is exclusive to your institution.',
                                        'During Skill Genie Bootcamp enrollment or credit purchase, enter this coupon code.',
                                        'The system will automatically validate the coupon and apply the eligible scholarship percentage to the total payable amount.',
                                        'The coupon can be redeemed only once and cannot be regenerated.',
                                    ].map((txt, i) => (
                                        <li key={i} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-green-500 mt-0.5 flex-shrink-0"/>{txt}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )}

        {error && (
            <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md animate-slideUp">
                <div className="bg-white border-l-4 border-red-500 rounded-xl shadow-2xl p-4 flex items-center gap-4 mx-4">
                    <div className="p-2 bg-red-50 rounded-lg text-red-600">
                        <AlertCircle size={20} />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-bold text-gray-800">Error</p>
                        <p className="text-xs text-gray-500 font-medium">{error}</p>
                    </div>
                    <button 
                        onClick={() => setError(null)} 
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        )}

        {success && (
            <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md animate-slideUp">
                <div className="bg-white border-l-4 border-green-500 rounded-xl shadow-2xl p-4 flex items-center gap-4 mx-4">
                    <div className="p-2 bg-green-50 rounded-lg text-green-600">
                        <CheckCircle2 size={20} />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-bold text-gray-800">Success</p>
                        <p className="text-xs text-gray-500 font-medium">{success}</p>
                    </div>
                    <button 
                        onClick={() => setSuccess(null)} 
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        )}
        </main>
      </div>
      
      {/* Confirmation and retry modals removed — scheduling is now automatic on nomination */}

      {showUnsavedModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
              <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl">
                  <h3 className="text-xl font-black text-gray-900 mb-2">Unsaved Changes</h3>
                  <p className="text-gray-500 mb-8">You have unsaved changes. Leave without saving?</p>
                  <div className="flex gap-4">
                      <button onClick={() => setShowUnsavedModal(false)} className="flex-1 py-3 rounded-xl font-bold text-gray-400 hover:bg-gray-50">Cancel</button>
                      <button onClick={() => {
                          setHasUnsavedChanges(false);
                          setShowUnsavedModal(false);
                          // In a real router, we would proceed to pendingPath
                      }} className="flex-1 py-3 rounded-xl font-bold text-red-500 hover:bg-red-50">Leave</button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
};

export default CollegeChallenge;
